export const DOC_SECTIONS = [
  "getting-started",
  "accounts",
  "workspace-setup",
  "workspace-settings",
  "roles",
  "super-admin",
  "home",
  "report",
  "dashboards",
  "widgets",
  "anomalies",
  "scheduled-reports",
  "data-sources",
  "warehouse",
  "data-prep",
  "ai-context",
  "logs",
  "use-case-flows",
] as const;

export type DocSectionId = (typeof DOC_SECTIONS)[number];

export type DocTocGroupId =
  | "start"
  | "workspace"
  | "access"
  | "product"
  | "reference";

export type DocTocGroup = {
  id: DocTocGroupId;
  /** i18n key under docs.groups */
  labelKey: string;
  sections: DocSectionId[];
};

/** Grouped sidebar navigation for the SaaS user guide. */
export const DOC_TOC_GROUPS: DocTocGroup[] = [
  {
    id: "start",
    labelKey: "start",
    sections: ["getting-started", "accounts"],
  },
  {
    id: "workspace",
    labelKey: "workspace",
    sections: ["workspace-setup", "workspace-settings"],
  },
  {
    id: "access",
    labelKey: "access",
    sections: ["roles", "super-admin"],
  },
  {
    id: "product",
    labelKey: "product",
    sections: [
      "home",
      "report",
      "dashboards",
      "widgets",
      "anomalies",
      "scheduled-reports",
      "data-sources",
      "warehouse",
      "data-prep",
      "ai-context",
      "logs",
    ],
  },
  {
    id: "reference",
    labelKey: "reference",
    sections: ["use-case-flows"],
  },
];

export type DocTocChild = {
  /** Fragment without leading #; matches the heading id rendered on the page. */
  id: string;
  /** Visible heading text (without the `{#id}` marker). */
  text: string;
};

const EXPLICIT_ID = /\s*\{#([A-Za-z0-9_-]+)\}\s*$/;

/** Splits a heading's raw text into visible text and its id (explicit `{#id}` wins over a slug). */
export function parseDocHeading(raw: string): { text: string; id: string } {
  const trimmed = raw.trim();
  const match = trimmed.match(EXPLICIT_ID);
  const text = match ? trimmed.replace(EXPLICIT_ID, "").trim() : trimmed;
  return { text, id: match ? match[1] : docsHeadingId(text) };
}

/** Second-level headings of a doc page, for the nested table of contents (code fences are skipped). */
export function extractDocHeadings(markdown: string): DocTocChild[] {
  const out: DocTocChild[] = [];
  let inFence = false;
  for (const line of markdown.split(/\r?\n/)) {
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      const { text, id } = parseDocHeading(m[1]);
      if (text) out.push({ id, text });
    }
  }
  return out;
}

export const DEFAULT_DOC_SECTION: DocSectionId = "getting-started";

export const isDocSection = (value: string | undefined): value is DocSectionId =>
  Boolean(value && (DOC_SECTIONS as readonly string[]).includes(value));

const TR_ASCII: Record<string, string> = {
  ç: "c",
  Ç: "c",
  ğ: "g",
  Ğ: "g",
  ı: "i",
  İ: "i",
  I: "i",
  ö: "o",
  Ö: "o",
  ş: "s",
  Ş: "s",
  ü: "u",
  Ü: "u",
};

/** Stable heading id from visible heading text (matches DocsLayout markdown headings). */
export function docsHeadingId(text: string): string {
  const ascii = text
    .split("")
    .map((ch) => TR_ASCII[ch] ?? ch)
    .join("");
  return ascii
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[`"'()[\]{}?]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
